import { useEffect, useState } from 'react';
import api from '../api/axios';
import styles from './Table.module.css';
import ConfirmDialog from '../components/ConfirmDialog';

const empty = {
  name: '',
  description: '',
  price: '',
  currency: 'INR',
  durationDays: 30,
  features: '',
  maxActiveAds: 0,
  featuredSlots: 0,
  bumpCredits: 0,
  isPopular: false,
  isActive: true,
  order: 0,
};

function formatPrice(pkg) {
  const n = Number(pkg.price) || 0;
  const cur = pkg.currency || 'INR';
  if (cur === 'INR') return `₹${n.toLocaleString('en-IN')}`;
  return `${cur} ${n.toLocaleString()}`;
}

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'add' | package object
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/packages');
      setPackages(data.packages || []);
    } catch {
      setError('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setForm(empty);
    setModal('add');
    setError('');
  };

  const openEdit = (pkg) => {
    setForm({
      name: pkg.name || '',
      description: pkg.description || '',
      price: pkg.price != null ? String(pkg.price) : '',
      currency: pkg.currency || 'INR',
      durationDays: pkg.durationDays ?? 30,
      features: (pkg.features || []).join('\n'),
      maxActiveAds: pkg.maxActiveAds ?? 0,
      featuredSlots: pkg.featuredSlots ?? 0,
      bumpCredits: pkg.bumpCredits ?? 0,
      isPopular: !!pkg.isPopular,
      isActive: pkg.isActive !== false,
      order: pkg.order ?? 0,
    });
    setModal(pkg);
    setError('');
  };

  const closeModal = () => {
    setModal(null);
    setForm(empty);
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        price: form.price,
        currency: form.currency,
        durationDays: Number(form.durationDays),
        features: form.features,
        maxActiveAds: Number(form.maxActiveAds),
        featuredSlots: Number(form.featuredSlots),
        bumpCredits: Number(form.bumpCredits),
        isPopular: !!form.isPopular,
        isActive: !!form.isActive,
        order: Number(form.order),
      };
      if (modal === 'add') {
        await api.post('/packages', payload);
      } else {
        await api.put(`/packages/${modal._id}`, payload);
      }
      closeModal();
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (pkg) => {
    setConfirm({
      message: `Delete package "${pkg.name}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api.delete(`/packages/${pkg._id}`);
          load();
        } catch {
          alert('Delete failed');
        }
      },
    });
  };

  const toggleActive = async (pkg) => {
    try {
      await api.patch(`/packages/${pkg._id}/toggle`);
      load();
    } catch {
      alert('Update failed');
    }
  };

  return (
    <div>
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      <div className={styles.header}>
        <h2 className={styles.title}>
          Packages{' '}
          <span style={{ fontSize: 14, color: '#888', fontWeight: 400 }}>
            ({packages.length})
          </span>
        </h2>
        <button type="button" className={styles.addBtn} onClick={openAdd}>
          + Add Package
        </button>
      </div>
      <p style={{ color: '#888', fontSize: 13, marginTop: -12, marginBottom: 16 }}>
        <strong>Duration (days)</strong> is how long each ad stays live when this package is
        assigned to a user. Assign packages from a user&apos;s detail page.
      </p>

      {loading ? (
        <div className={styles.center}>Loading...</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Price</th>
                <th>Duration</th>
                <th>Limits</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {packages.length === 0 && (
                <tr>
                  <td colSpan={7} className={styles.empty}>
                    No packages yet. Create one to show in Buy Package.
                  </td>
                </tr>
              )}
              {packages.map((pkg) => (
                <tr key={pkg._id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {pkg.name}
                      {pkg.isPopular && (
                        <span className={styles.badgeFeatured} style={{ marginLeft: 8 }}>
                          Popular
                        </span>
                      )}
                    </div>
                    {pkg.description && (
                      <div
                        style={{
                          fontSize: 12,
                          color: '#888',
                          marginTop: 2,
                          maxWidth: 260,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={pkg.description}
                      >
                        {pkg.description}
                      </div>
                    )}
                    {(pkg.features || []).length > 0 && (
                      <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                        {(pkg.features || []).slice(0, 2).join(' · ')}
                        {(pkg.features || []).length > 2 ? '…' : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ fontWeight: 700 }}>{formatPrice(pkg)}</td>
                  <td>{pkg.durationDays} days</td>
                  <td style={{ fontSize: 12, color: '#555' }}>
                    <div>
                      Ads: {pkg.maxActiveAds === 0 ? 'Unlimited' : pkg.maxActiveAds}
                    </div>
                    <div>Featured: {pkg.featuredSlots || 0}</div>
                    <div>Bumps: {pkg.bumpCredits || 0}</div>
                  </td>
                  <td>{pkg.order ?? 0}</td>
                  <td>
                    <span
                      className={pkg.isActive ? styles.badgeActive : styles.badgeInactive}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleActive(pkg)}
                      title="Click to toggle"
                    >
                      {pkg.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className={styles.actions}>
                    <button
                      type="button"
                      className={styles.editBtn}
                      onClick={() => openEdit(pkg)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(pkg)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className={styles.overlay} onClick={closeModal}>
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 520 }}
          >
            <h3>{modal === 'add' ? 'Add Package' : 'Edit Package'}</h3>
            {error && <div className={styles.formError}>{error}</div>}
            <form onSubmit={handleSave} className={styles.form}>
              <label>Name *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Silver, Gold, Premium"
                required
              />
              <label>Description</label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short summary shown to users"
              />
              <div className={styles.formRow2}>
                <div>
                  <label>Price *</label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label>Currency</label>
                  <select
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  >
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                  </select>
                </div>
              </div>
              <label>Duration (days) * — ad expiry when user has this package</label>
              <input
                type="number"
                min="1"
                value={form.durationDays}
                onChange={(e) =>
                  setForm({ ...form, durationDays: e.target.value })
                }
                required
              />
              <label>Display order</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
              />
              <div className={styles.formRow2}>
                <div>
                  <label>Max active ads (0 = unlimited)</label>
                  <input
                    type="number"
                    min="0"
                    value={form.maxActiveAds}
                    onChange={(e) =>
                      setForm({ ...form, maxActiveAds: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label>Featured slots</label>
                  <input
                    type="number"
                    min="0"
                    value={form.featuredSlots}
                    onChange={(e) =>
                      setForm({ ...form, featuredSlots: e.target.value })
                    }
                  />
                </div>
              </div>
              <label>Bump credits</label>
              <input
                type="number"
                min="0"
                value={form.bumpCredits}
                onChange={(e) => setForm({ ...form, bumpCredits: e.target.value })}
              />
              <label>Features (one per line)</label>
              <textarea
                rows={4}
                value={form.features}
                onChange={(e) => setForm({ ...form, features: e.target.value })}
                placeholder={'Priority in search\nFeatured badge\nMore bumps'}
              />
              <label className={styles.checkLabel}>
                <input
                  type="checkbox"
                  checked={form.isPopular}
                  onChange={(e) =>
                    setForm({ ...form, isPopular: e.target.checked })
                  }
                />
                Mark as Popular
              </label>
              {modal !== 'add' && (
                <label className={styles.checkLabel}>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm({ ...form, isActive: e.target.checked })
                    }
                  />
                  Active
                </label>
              )}
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.saveBtn}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
